import React from 'react'
import { isEmpty, slice } from 'lodash'
import moment from 'moment-timezone'
import Col from 'antd/lib/col'
import Row from 'antd/lib/row'
import Tag from 'antd/lib/tag'
import Table from 'antd/lib/table'
import Avatar from 'antd/lib/avatar'
import Select from 'antd/lib/select'
import Switch from 'antd/lib/switch'
import Tooltip from 'antd/lib/tooltip'
import Skeleton from 'antd/lib/skeleton'
import InputNumber from 'antd/lib/input-number'
import { ClockCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import env from 'env'
import SnoozeButton from './SnoozeButton'
import { isItemSnoozed, getAllOutOfStockGroups } from './utils'
import { isCategorySpeciallyAvailable } from '../helpers'
import { CutOffIcon } from '@slerp/assets'
import { StoreVariant, StoreInfo } from '../types'
import { ProductCategory } from 'components/ProductsAndModifiers/Products/Form'
import styled from '@emotion/styled'
import { isAdmin, isManager, useSession } from 'packages/@slerp/accounts'
import { AvailabilityAction } from './types'
import { Cell } from './styles'
import { CommonTag } from 'components/Common'

interface VariantsInventoryTableProps {
  storeVariants: StoreVariant[]
  storeInfo: StoreInfo
  isPreorder?: boolean
  isLoading: boolean
  onToggle: (action: AvailabilityAction, id: string, name: string) => void
  onStockCountUpdate: (
    fromStockType: boolean,
    id: string,
    name: string,
    params: {
      variantId: string
      stockType: string | null
      stockCount: number
      stockSold: number
      stockResetCount: number
    }
  ) => void
  onSelectVariant: (storeVariant: StoreVariant) => void
  processingVariantStockToggle: string[]
  isVariantTogglingStockState: boolean
  variantStockCountLoading: string[]
  snoozingStoreVariant: boolean
  unsnoozingStoreVariant: boolean
  categoriesWithCutOffRules: string[]
  selectedRowKeys: string[]
  onSelectedRowKeysChange: (keys: any) => void
}

const VariantsInventoryTable = ({
  storeVariants,
  storeInfo,
  isPreorder,
  isLoading,
  onToggle,
  onStockCountUpdate,
  onSelectVariant,
  processingVariantStockToggle,
  isVariantTogglingStockState,
  variantStockCountLoading,
  snoozingStoreVariant,
  unsnoozingStoreVariant,
  categoriesWithCutOffRules,
  selectedRowKeys,
  onSelectedRowKeysChange
}: VariantsInventoryTableProps) => {
  const { user } = useSession()
  const canManage = isAdmin(user) || isManager(user)

  const allOutOfStockGroups = React.useMemo(
    () =>
      getAllOutOfStockGroups(
        storeVariants.map((sv) => ({
          in_stock: sv.in_stock,
          availability_group: sv.product_variant?.product?.availability_group
        }))
      ),
    [storeVariants]
  )

  const constructParams = (variant: StoreVariant) => {
    return {
      variantId: variant.variant_id,
      stockType: variant.stock_type || '',
      stockCount: 0,
      stockSold: 0,
      stockResetCount: 0
    }
  }

  const StockReset = ({ variant }: { variant: StoreVariant }) => {
    const isStockLoading = variantStockCountLoading.includes(variant.id)
    const params = constructParams(variant)

    const options = [
      { value: '', label: 'Disabled' },
      { value: 'manual', label: 'Manual' },
      { value: 'daily', label: 'Daily' },
      { value: 'weekly_monday', label: 'Weekly (Monday)' },
      { value: 'weekly_tuesday', label: 'Weekly (Tuesday)' },
      { value: 'weekly_wednesday', label: 'Weekly (Wednesday)' },
      { value: 'weekly_thursday', label: 'Weekly (Thursday)' },
      { value: 'weekly_friday', label: 'Weekly (Friday)' },
      { value: 'weekly_saturday', label: 'Weekly (Saturday)' },
      { value: 'weekly_sunday', label: 'Weekly (Sunday)' }
    ]

    if (isStockLoading)
      return <Skeleton.Input style={{ width: '160px' }} active />

    return (
      <Select
        loading={isStockLoading}
        disabled={!canManage || isStockLoading}
        value={variant.stock_type || ''}
        style={{ width: '160px' }}
        onSelect={(value) => {
          if (value !== '') {
            params.stockResetCount = variant.stock_reset_count || 0
            params.stockSold = variant.stock_sold
            params.stockCount = variant.stock_count
          }
          onStockCountUpdate(true, variant.id, variant.product_variant.name, {
            ...params,
            stockType: value
          })
        }}
        data-testid='inventory-dashboard-stock-reset'
      >
        {options.map((opt) => (
          <Select.Option
            title={opt.label}
            value={opt.value}
            label={opt.label}
            disabled={
              isPreorder && opt.value !== 'daily' && opt.label !== 'Disabled'
            }
          >
            {opt.label}
          </Select.Option>
        ))}
      </Select>
    )
  }

  const StockCount = ({ variant }: { variant: StoreVariant }) => {
    const isStockLoading = variantStockCountLoading.includes(variant.id)
    const params = constructParams(variant)

    if (isStockLoading)
      return <Skeleton.Input size='default' style={{ width: '88px' }} active />

    return (
      <InputNumber
        data-testid='inventory-dashboard-stock-count-input'
        disabled={isStockLoading || (variant.stock_type || '') === ''}
        defaultValue={variant.stock_reset_count || 0}
        min={0}
        className='_max-w-sm'
        onBlur={(value) => {
          if (Number(value.target.value) >= 0)
            onStockCountUpdate(
              false,
              variant.id,
              variant.product_variant.name,
              {
                ...params,
                stockResetCount: Number(value.target.value) || 0,
                stockCount: Number(value.target.value) || 0
              }
            )
        }}
      />
    )
  }

  const STORE_VARIANT_COLUMNS = [
    {
      title: 'Product Name',
      dataIndex: ['product_variant', 'name'],
      key: 'key',
      width: '20%',
      render: (productName: string, storeVariant: StoreVariant) => {
        const { variant_id } = storeVariant
        const imageUrl = `https://gear.${
          env.ASSET_HOST
        }/assets/variant/${variant_id}?size=original&${moment().unix()}`
        return (
          <>
            <Row align='middle' gutter={[8, 8]} className='_mb-0'>
              <Col>
                <Avatar size={30} shape='square' src={imageUrl} />
              </Col>
              <Col span={20}>
                <span
                  data-testid='inventory-dashboard-store-pricing-link'
                  className='cursor-pointer'
                  onClick={() => onSelectVariant(storeVariant)}
                >
                  {productName}
                </span>
              </Col>
            </Row>
          </>
        )
      }
    },
    {
      title: 'Category',
      dataIndex: ['categories'],
      key: 'category',
      width: '20%',
      render: (categories: ProductCategory[]) => {
        let aggregatedViewEnabled = false
        let visibleGroups = []
        let aggregatedCategories: ProductCategory[] = []

        if ((categories || []).length === 3) {
          visibleGroups = [...categories]
        } else {
          aggregatedViewEnabled = (categories || []).length > 2
          visibleGroups = aggregatedViewEnabled
            ? slice(categories, 0, 2)
            : [...categories]
          aggregatedCategories = slice(categories || [], 2)
        }

        const tags = visibleGroups.map((pc: ProductCategory) => {
          const isSpeciallyAvailable = isCategorySpeciallyAvailable({
            productCategory: pc,
            isPreorder: isPreorder ?? false,
            storeId: storeInfo.id
          })

          const hasCutOffRule =
            categoriesWithCutOffRules.includes(pc.category_id) &&
            Boolean(isPreorder)

          const tooltipTitle = {
            'true-true':
              'This category has both a Special Availability and a Category Cut-Off rule applied.',
            'true-false':
              'This category has a Special Availability rule applied.',
            'false-true': 'This category has a Category Cut-Off rule applied.',
            'false-false': ''
          }[`${isSpeciallyAvailable}-${hasCutOffRule}`]

          return (
            <Tooltip title={tooltipTitle}>
              <StyledTag
                isSpeciallyAvailable={isSpeciallyAvailable}
                hasCutOffRule={hasCutOffRule}
              >
                {(isSpeciallyAvailable || hasCutOffRule) && (
                  <span>
                    {isSpeciallyAvailable && (
                      <ClockCircleOutlined width={12} height={12} />
                    )}
                    {hasCutOffRule && <CutOffIcon width={12} height={12} />}
                  </span>
                )}
                {pc.category.name}
              </StyledTag>
            </Tooltip>
          )
        })

        if (aggregatedViewEnabled) {
          tags.push(
            <Tooltip
              title={
                <ul className='_pl-16 _mb-0'>
                  {aggregatedCategories.map((pc: ProductCategory) => {
                    const isSpeciallyAvailable = isCategorySpeciallyAvailable({
                      productCategory: pc,
                      isPreorder: isPreorder ?? false,
                      storeId: storeInfo.id
                    })

                    return (
                      <li
                        className={`${
                          isSpeciallyAvailable ? '-specially-available' : ''
                        }`}
                      >
                        {pc.category.name}
                      </li>
                    )
                  })}
                </ul>
              }
              placement='right'
            >
              <Tag className='-basic -plain-text-format'>
                {`+ ${aggregatedCategories.length} more categories`}
              </Tag>
            </Tooltip>
          )
        }

        return (
          <Row gutter={[8, 8]} className='_mb-0'>
            {tags}
          </Row>
        )
      }
    },
    {
      title: 'SKU',
      dataIndex: ['product_variant', 'sku'],
      key: 'product_variant.sku',
      width: '10%'
    },
    {
      title: (
        <>
          Reset{' '}
          <Tooltip
            title={
              <span>
                The reset function allows you to set when your stock count
                returns to par levels. This can be set to manual, daily or to a
                specific day of the week in the dropdown below. Learn about
                stock count{' '}
                <a
                  href='https://support.slerp.com/knowledge/understanding-inventories'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  here
                </a>
                .
              </span>
            }
          >
            <QuestionCircleOutlined className='_ml-8 _mr-8' />
          </Tooltip>
        </>
      ),
      key: 'stock_type',
      width: '160px',
      render: (record: StoreVariant) => <StockReset variant={record} />
    },
    {
      key: 'divider',
      width: '5%',
      render: () => <div>to</div>
    },
    {
      title: (
        <>
          <span>Default reset amount</span>
          <Tooltip
            placement='right'
            title={
              <a
                href='https://support.slerp.com/en_us/stock-counts-v1-B1HPD54pu'
                target='_blank'
                rel='noopener noreferrer'
              >
                Stock Counts
              </a>
            }
          >
            <QuestionCircleOutlined className='_ml-8' />
          </Tooltip>
        </>
      ),
      key: 'stock_count',
      width: 160,
      render: (record: StoreVariant) => <StockCount variant={record} />
    },
    isPreorder
      ? {}
      : {
          title: 'Remaining stock',
          key: 'left_in_stock',
          render: (variant: StoreVariant) => {
            const { id, stock_type, stock_count, stock_sold } = variant
            const isStockLoading = variantStockCountLoading.includes(id)
            if (isStockLoading)
              return <Skeleton.Input style={{ width: '88px' }} active />

            const diff = (Number(stock_count) || 0) - (Number(stock_sold) || 0)
            const available = isEmpty(stock_type) ? 0 : Math.max(0, diff)

            return (
              <InputNumber
                data-testid='inventory-dashboard-available-stock'
                disabled={true}
                value={available}
                className='_max-w-sm'
              />
            )
          },
          width: 160
        },
    {
      title: 'Availability group',
      key: 'availability_group',
      width: '12%',
      sorter: (a: StoreVariant, b: StoreVariant) =>
        (a.product_variant?.product?.availability_group || '').localeCompare(
          b.product_variant?.product?.availability_group || ''
        ),
      render: (record: StoreVariant) => {
        const group = record.product_variant?.product?.availability_group
        return (
          group && (
            <Cell>
              <CommonTag
                type={allOutOfStockGroups.has(group) ? 'active' : 'default'}
              >
                {group}
              </CommonTag>
            </Cell>
          )
        )
      }
    },
    {
      title: 'In Stock',
      dataIndex: 'in_stock',
      key: 'in_stock',
      width: 240,
      render: (
        inStock: boolean,
        { id, variant_id, product_variant, unsnooze_at }: StoreVariant
      ) => {
        const loading = processingVariantStockToggle.includes(variant_id)
        const snoozed = isItemSnoozed(unsnooze_at)
        return (
          <Switch
            loading={loading}
            checked={inStock}
            disabled={!canManage || isVariantTogglingStockState || snoozed}
            onChange={(value: boolean) =>
              onToggle(
                value
                  ? AvailabilityAction.IN_STOCK
                  : AvailabilityAction.OUT_OF_STOCK,
                id,
                product_variant.name
              )
            }
          />
        )
      }
    }
  ]

  const VARIANT_SNOOZE_COLUMN = canManage
    ? [
        {
          title: (
            <>
              Snooze
              <Tooltip
                title={
                  <span>
                    Product snooze allows you to mark a product temporarily out
                    of stock. For more information read{' '}
                    <a
                      href='https://support.slerp.com/en_us/product-snooze-BJWZXzfoc'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      here
                    </a>
                    .
                  </span>
                }
              >
                <QuestionCircleOutlined className='_ml-8 _mr-8' />
              </Tooltip>
            </>
          ),
          dataIndex: 'snooze',
          key: 'snooze',
          width: 160,
          render: (
            val: undefined | null,
            { id, variant_id, product_variant, unsnooze_at }: StoreVariant
          ) => {
            return (
              <SnoozeButton
                key={variant_id}
                itemId={id}
                name={product_variant.name}
                unsnoozeAt={unsnooze_at}
                onClickHandler={onToggle}
                loading={snoozingStoreVariant || unsnoozingStoreVariant}
                disabled={snoozingStoreVariant || unsnoozingStoreVariant}
                dataTestId='inventory-dashboard-snooze-product'
              />
            )
          }
        }
      ]
    : []

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectedRowKeysChange
  }

  return (
    <Table
      loading={isLoading}
      data-testid='inventory-dashboard-variants-inventory-table'
      rowSelection={rowSelection as any}
      columns={
        !isPreorder
          ? [...STORE_VARIANT_COLUMNS, ...VARIANT_SNOOZE_COLUMN]
          : STORE_VARIANT_COLUMNS
      }
      dataSource={storeVariants}
      pagination={{
        defaultPageSize: 50,
        showSizeChanger: true
      }}
      rowKey='id'
    />
  )
}

const StyledTag = styled(Tag)<{
  isSpeciallyAvailable: boolean
  hasCutOffRule: boolean
}>`
  ${({ isSpeciallyAvailable, hasCutOffRule }) => `
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${
    isSpeciallyAvailable || hasCutOffRule ? '#F3E4F7' : '#E7E5E9'
  };
  padding: 4px 12px;

  color: ${isSpeciallyAvailable || hasCutOffRule ? '#D092DD' : '#8C8CA1'};
  font-weight: 500;
  border: 1px solid
    ${isSpeciallyAvailable || hasCutOffRule ? '#D092DD' : 'transparent'};
  font-size: 12px;
  line-height: 1.5;

  & > span {
    position: relative;
    top: -1px;

    display: flex;
    align-items: center;
    height: 100%;
    gap: 2px;
  }

  `}
`

export default VariantsInventoryTable
